/**
 * The redirect behind the app's `location.assign`/`location.replace`, and the
 * means for an app restart to take a redirect that is still pending.
 *
 * Same-origin redirects are applied when the app restarts (see the
 * history.pushState/replaceState calls in main.tsx), which keeps the SPA — and
 * the tokens it just stored — alive. Cross-origin ones (OAuth provider URLs,
 * third-party redirect URIs) have no such path and need a real navigation. So
 * does a same-origin redirect that no restart takes, because the user did not
 * change: a login as the user whose tokens the browser already holds, or a
 * logout from a browser that only held the session cookie.
 *
 * Every path keeps the caller's choice between a new history entry (`assign`)
 * and none (`replace`): logging in replaces /login, so Back skips over it.
 */
export interface Redirect {
  url: string;
  replace: boolean;
}

export function createRedirect(
  context: Window = window,
): [
  redirect: (url: string, replace: boolean) => void,
  take: () => Redirect | undefined,
] {
  let pending: Redirect | undefined;

  const navigate = ({ url, replace }: Redirect) =>
    replace ? context.location.replace(url) : context.location.assign(url);

  const redirect = (url: string, replace: boolean) => {
    const target = { url, replace };
    const { origin } = new URL(url, context.location.href);
    if (origin !== context.location.origin) {
      navigate(target);
      return;
    }
    pending = target;
    // Auth callbacks redirect in the same task that applies the new tokens, so
    // any restart has taken the URL by now, and the tokens are stored.
    setTimeout(() => {
      if (pending !== target) return;
      pending = undefined;
      navigate(target);
    });
  };

  const take = () => {
    const target = pending;
    pending = undefined;
    return target;
  };

  return [redirect, take];
}
